import { useMemo } from 'react';

import { DefaultError, InfiniteData, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';

import AnalyticsResponse from './AnalyticsResponse';

type QueryResult = Awaited<ReturnType<typeof retrieveAnalytics>>;

export type InsightsQueryParams = {
  source: Sources;
  dimensions?: (Dimensions | string)[];
  metrics: (Metrics | string)[];
  sortBy?: string;
  rollups?: boolean;
  accessModes?: string[];
  reviewType?: string;
  dimensionDisplayNames?: Record<string, string>;
};

type Options = {
  queryKey?: string;
  enabled?: boolean;
  // Additional filters merged on top of the dashboard's filters. Used by the slideout to lock
  // results to a single entity without mutating shared dashboard state.
  extraFilters?: Record<string, unknown> | null;
  // Keep the previous result visible while a *new* query key fetches (e.g. a slideout View By
  // change), instead of resetting to empty/pending. Lets readers whose value is the same across
  // query keys keep showing it without a spinner (they gate on `initialLoading`); readers whose
  // value changes still show a spinner by gating on `loading`. Opt-in — off by default so other
  // consumers keep the reset-on-refetch behavior.
  keepPreviousData?: boolean;
};

const defaultOptions = {
  getNextPageParam: () => null,
  initialPageParam: null,
};

export default function useInsightsAnalytics(
  queryParams: InsightsQueryParams,
  baseFilters: Record<string, unknown> | null,
  {
    queryKey = 'insightsAnalytics',
    enabled = true,
    extraFilters = null,
    keepPreviousData: retainPrevious = false,
  }: Options = {}
) {
  const analyticsFilters = useMemo(
    () => (baseFilters ? { ...baseFilters, ...(extraFilters ?? {}) } : null),
    [baseFilters, extraFilters]
  );

  const query = useInfiniteQuery<QueryResult, DefaultError, InfiniteData<Analytics, Analytics>>({
    queryKey: [queryKey, queryParams, analyticsFilters],
    queryFn: async ({ pageParam }) =>
      retrieveAnalytics({ ...queryParams, ...(pageParam as InsightsQueryParams) }, analyticsFilters),
    enabled: enabled && !!analyticsFilters,
    ...(retainPrevious ? { placeholderData: keepPreviousData } : {}),
    ...defaultOptions,
  });

  const response = useMemo(() => new AnalyticsResponse(query.data, false), [query.data]);

  return {
    // `loading` is true on the first load and (when retaining previous data) during a query-key
    // switch that's showing stale placeholder data — for readers whose value changes per key.
    loading: query.isPending || (retainPrevious ? query.isPlaceholderData : query.isRefetching),
    // `initialLoading` is only the very first load (no data yet) — for readers whose value is the
    // same across keys and can keep showing the retained previous value during a switch.
    initialLoading: query.isPending,
    query,
    data: response.data,
    rollups: response.rollups,
  };
}
